import React, { useState, useMemo, useEffect } from 'react';
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import FocusLock from 'react-focus-lock';
import styles from './BaseModal.scss.js';

const MODAL_CONTAINER_ID = 'np-modals-container';
const MODAL_ANIMATION_DURATION = 200;
let VerticalPositionModes = /*#__PURE__*/function (VerticalPositionModes) {
  VerticalPositionModes["normal"] = "normal";
  VerticalPositionModes["top"] = "flex-start";
  VerticalPositionModes["center"] = "center";
  VerticalPositionModes["bottom"] = "flex-end";
  return VerticalPositionModes;
}({});
let BackdropBackgroundModes = /*#__PURE__*/function (BackdropBackgroundModes) {
  BackdropBackgroundModes["default"] = "rgba(21, 45, 57, 0.9)";
  BackdropBackgroundModes["whiteGradient"] = "linear-gradient(180deg, rgba(255,255,255,0.7) 0%, #FFFFFF 100%)";
  return BackdropBackgroundModes;
}({});
/**
 * @deprecated In favor of the new Modal of the BCL, see (https://nearpod-packages.gitlab-pages.nearpod.com/base-component-library/?path=/docs/library-modal--docs)
 */
const BaseModal = _ref => {
  let {
    isOpen = false,
    backdrop = 'on',
    backdropBackground = BackdropBackgroundModes.default,
    onBackdropClick = () => {},
    children,
    isScrollable = false,
    containerRef,
    verticalPositionOnContainer = containerRef ? VerticalPositionModes.center : VerticalPositionModes.normal,
    disableFocusTrap = false
  } = _ref;
  const hasCustomContainer = !!containerRef?.current;
  const [isContainerReady, setIsContainerReady] = useState(false);

  // Get or create the modals container
  const modalsContainer = useMemo(() => {
    let auxModalsContainer;

    // If the caller provided a 'containerRef' use it
    if (hasCustomContainer) {
      auxModalsContainer = containerRef.current;
      // else, try to get MODAL_CONTAINER_ID DOM node
    } else {
      auxModalsContainer = document.getElementById(MODAL_CONTAINER_ID);
    }
    if (!auxModalsContainer) {
      auxModalsContainer = document.createElement('div');
      auxModalsContainer.setAttribute('id', MODAL_CONTAINER_ID);
      document.body.appendChild(auxModalsContainer);
      if (isScrollable) {
        auxModalsContainer.className = styles['modal-container--scrollable'];
      }
    }
    return auxModalsContainer;
  }, [containerRef, hasCustomContainer, isScrollable]);
  useEffect(() => () => {
    enableBodyScroll(document.body);
  }, []);

  // update isContainerReady when containerRef changes
  useEffect(() => {
    // if not custom container exist set it as ready
    if (!containerRef) {
      setIsContainerReady(true);
      // if some custom container exist set it as ready when the DOM Node be ready
    } else {
      setIsContainerReady(!!containerRef?.current);
    }
  }, [containerRef]);

  // Handle the scroll lock
  useEffect(() => {
    if (isOpen && backdrop !== 'off') {
      disableBodyScroll(document.body, {
        reserveScrollBarGap: true
      });
    } else if (!isOpen && backdrop !== 'off') {
      enableBodyScroll(document.body);
    }
  }, [isOpen, backdrop]);

  // Handle the Escape key press
  useEffect(() => {
    const handleEscapeKey = event => {
      if (isOpen && event.key === 'Escape') {
        onBackdropClick();
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onBackdropClick]);

  // don't render anything until containerRef is ready
  if (!isContainerReady) {
    return null;
  }
  return /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement(CSSTransition, {
    in: isOpen,
    timeout: MODAL_ANIMATION_DURATION,
    unmountOnExit: true,
    classNames: {
      enter: styles['modal-enter'],
      enterActive: styles['modal-enter-active'],
      enterDone: styles['modal-enter-done'],
      exit: styles['modal-exit'],
      exitActive: styles['modal-exit-active'],
      exitDone: styles['modal-exit-done']
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      alignItems: verticalPositionOnContainer
    },
    className: classNames(styles.content, {
      [styles['content--has-custom-container']]: hasCustomContainer
    })
  }, backdrop !== 'off' && /*#__PURE__*/React.createElement("div", {
    onClick: backdrop === 'on' ? onBackdropClick : undefined,
    style: {
      background: backdropBackground
    },
    className: classNames(styles.content__backdrop, {
      [styles['content__backdrop--has-custom-container']]: hasCustomContainer
    })
  }), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.content__modal, {
      [styles['content__modal--scrollable']]: isScrollable,
      [styles['content__modal--has-custom-container']]: hasCustomContainer
    })
  }, /*#__PURE__*/React.createElement(FocusLock, {
    autoFocus: false,
    disabled: disableFocusTrap
  }, children)))), modalsContainer);
};
var BaseModal$1 = BaseModal;

export { BackdropBackgroundModes, VerticalPositionModes, BaseModal$1 as default };
//# sourceMappingURL=BaseModal.js.map
